/**
 * The incident form, rendered inside the conversation.
 *
 * The incident twin of `request-form-card.tsx`. Three decisions carry it:
 *
 *   1. The questions are not written here. `IncidentReporter` — the same
 *      component `/tickets/new` uses — owns them: describe it in your own
 *      words, say how much it is affecting your work, then answer 0-3 tailored
 *      follow-ups (plus a closing "Anything else?"). A second copy would drift within a release.
 *      The card passes `fromChat`: the conversation already did the asking, so
 *      it opens straight on review — the assistant's questions and answers (if
 *      any), "Anything else?" with attachments, and Submit. No second triage
 *      round of new questions, and never an automatic submit.
 *   2. The values arrive as **prefill**. The assistant has already been talking
 *      to the person, so it passes over their description, the urgency if it
 *      was clear, and any follow-up questions it already asked with their
 *      answers. Nothing it supplied is locked.
 *   3. Filing goes read-only the instant it succeeds, and the raised ticket is
 *      kept in the query cache rather than in component state: when a finished
 *      turn hands over from the live stream to the persisted transcript this
 *      card unmounts and remounts, and an incident that survived that as an
 *      enabled button is a duplicate ticket waiting to happen.
 *
 * Raised for a colleague (the person said so and the assistant found them, or
 * they chose "Reporting this for someone else?"), the ticket is theirs: the
 * receipt says "Raised for <name> — they'll get the updates" and offers no link
 * into a ticket the reporter cannot open.
 *
 * "Is this the same problem?" -> "Yes, add me to it" (8 Oct) files nothing: the
 * person is noted on someone else's open ticket, and the card says so. Kept
 * in the query cache like a raised ticket, so a remount cannot offer the form
 * again.
 *
 * Attachments travel from both directions: the screenshot dropped into the
 * composer is threaded onto the ticket, and the card carries its own uploader
 * for the one the assistant asks for after the card is already on screen.
 */

import { Icon, Wrench } from "@norican/icons";
import { Alert, Button, Card, cn, DescriptionList, Separator, Typography } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";

import {
  type IncidentCreateRequest,
  IncidentReporter,
  KioskTicketNumberNote,
  type TicketLevelCode,
} from "@/features/tickets";
import { newIdempotencyKey } from "@/features/tickets/api/operations";
import {
  type JoinedTicket,
  SimilarJoinedNotice,
} from "@/features/tickets/components/similar-ticket-prompt";
import type { OnBehalfPerson } from "@/features/tickets/lib/on-behalf";
import { useT } from "@/i18n";
import { logger } from "@/lib/logger";

import type { AssistantIncidentFormEvent, TicketRef } from "./contract";
import { assistantKeys, useRaiseIncident } from "./hooks";
import { useInlineForm } from "./inline-forms";
import { CancelledNote } from "./request-form-card";
import { TicketRefCard } from "./ticket-ref-card";

export interface IncidentFormCardProps {
  /** The streamed prefill. */
  form: AssistantIncidentFormEvent;
  /**
   * Identity of this card across a remount.
   *
   * Scopes the "already raised" memo in the query cache, so it must be stable
   * for the life of the conversation — the assistant message id plus this
   * form's position in the turn, which is append-only.
   */
  formKey: string;
  /** Threaded onto the ticket so it lands against the right conversation. */
  conversationId?: string | null;
  /** Draft attachment ids the user's message carried. */
  attachmentIds?: string[];
  className?: string;
}

export function IncidentFormCard({
  form,
  formKey,
  conversationId = null,
  attachmentIds = [],
  className,
}: IncidentFormCardProps) {
  const { t } = useT("assistant");
  const { t: tt } = useT("tickets");
  const queryClient = useQueryClient();
  const headingId = useId();

  const raise = useRaiseIncident();
  const [filed, setFiled] = useState<IncidentCreateRequest | null>(null);
  const [ticket, setTicket] = useState<TicketRef | null>(
    () => queryClient.getQueryData<TicketRef>(assistantKeys.incidentTicket(formKey)) ?? null,
  );
  const [raisedFor, setRaisedFor] = useState<string | null>(
    () => queryClient.getQueryData<string>(assistantKeys.incidentRaisedFor(formKey)) ?? null,
  );

  /**
   * The idempotency key for the attempt currently on screen.
   *
   * Same rule as `useTicketSubmit`: pressing Submit twice, or retrying after a
   * timeout, reuses the key and cannot produce a second ticket — while editing
   * and sending again is a genuinely new attempt and gets a fresh one. Keyed on
   * the reporter's fingerprint, which leaves out the subject and category
   * triage derived, so a re-triage between retries cannot mint a new key.
   */
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  /** Synchronous: `raise.isPending` lands a render too late for a double click. */
  const inFlight = useRef(false);

  const [joined, setJoined] = useState<JoinedTicket | null>(
    () => queryClient.getQueryData<JoinedTicket>(assistantKeys.incidentJoined(formKey)) ?? null,
  );
  const handleJoined = (value: JoinedTicket) => {
    queryClient.setQueryData<JoinedTicket>(assistantKeys.incidentJoined(formKey), value);
    setJoined(value);
  };

  const submitted = ticket !== null || joined !== null;
  // 14 Oct: Cancel closes the form without sending; the chat's composer comes back.
  const [cancelled, setCancelledState] = useState(
    () => queryClient.getQueryData<boolean>(assistantKeys.formCancelled(formKey)) === true,
  );
  const setCancelled = (value: boolean) => {
    queryClient.setQueryData(assistantKeys.formCancelled(formKey), value);
    setCancelledState(value);
  };
  useInlineForm(formKey, !submitted && !cancelled);

  const handleSubmit = async (
    body: IncidentCreateRequest,
    fingerprint: string,
    forPerson: OnBehalfPerson | null,
  ) => {
    if (submitted || inFlight.current) {
      return;
    }
    if (attempt.current === null || attempt.current.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, key: newIdempotencyKey() };
    }
    logger.debug("[assistant] raising inline incident", conversationId);

    inFlight.current = true;
    try {
      await raise.mutateAsync({
        formKey,
        body,
        idempotencyKey: attempt.current.key,
        forPerson,
      });
      const raised = queryClient.getQueryData<TicketRef>(assistantKeys.incidentTicket(formKey));
      logger.debug("[assistant] inline incident raised", raised?.ticket_number);
      setFiled(body);
      setRaisedFor(
        queryClient.getQueryData<string>(assistantKeys.incidentRaisedFor(formKey)) ?? null,
      );
      setTicket(raised ?? null);
    } catch (error) {
      // Rendered inline by the reporter; rethrowing would surface as an
      // unhandled rejection in the console and tell the user nothing.
      logger.warn("[assistant] could not raise the inline incident", error);
    } finally {
      inFlight.current = false;
    }
  };

  const urgency: TicketLevelCode | null = form.urgency_code ?? null;
  const onBehalfOf: OnBehalfPerson | null =
    form.on_behalf_of == null
      ? null
      : { email: form.on_behalf_of.email, displayName: form.on_behalf_of.display_name };

  return (
    <section aria-labelledby={headingId} className={cn("w-full", className)}>
      <Card variant="outline" accent="left" accentTone={submitted ? "success" : "accent"}>
        <Card.Header>
          <div className="flex min-w-0 items-start gap-3">
            <Icon icon={Wrench} size="md" tone={submitted ? "success" : "accent"} />
            <div className="flex min-w-0 flex-col gap-1">
              <Typography as="h3" variant="heading-5" id={headingId}>
                {joined !== null
                  ? tt("similar.joinedTitle")
                  : submitted
                    ? t("incidentForm.submittedTitle")
                    : t("incidentForm.title")}
              </Typography>
              <Typography variant="body-sm" tone="muted">
                {joined !== null
                  ? tt("similar.joinedSubtitle")
                  : submitted
                    ? t("incidentForm.submittedSubtitle")
                    : t("incidentForm.subtitle")}
              </Typography>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          <div className="flex flex-col gap-4">
            <Separator />

            {joined !== null ? (
              <SimilarJoinedNotice joined={joined} />
            ) : ticket !== null ? (
              <RaisedSummary
                body={filed}
                fallbackDetails={form.details ?? ""}
                ticket={ticket}
                raisedFor={raisedFor}
              />
            ) : cancelled ? (
              <CancelledNote onReopen={() => setCancelled(false)} />
            ) : (
              <>
                {attachmentIds.length === 0 ? null : (
                  <Typography variant="caption" tone="muted">
                    {t("incidentForm.attachments", { count: attachmentIds.length })}
                  </Typography>
                )}
                <IncidentReporter
                  initial={{
                    subject: form.subject,
                    details: form.details,
                    urgency,
                    followups: form.followups ?? [],
                    onBehalfOf,
                  }}
                  extraAttachmentIds={attachmentIds}
                  conversationId={conversationId}
                  fromChat
                  submitting={raise.isPending}
                  submitError={raise.isError ? raise.error : null}
                  onSubmit={handleSubmit}
                  onJoined={handleJoined}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    tone="neutral"
                    size="sm"
                    disabled={raise.isPending}
                    onClick={() => setCancelled(true)}
                  >
                    {t("inlineForm.cancel")}
                  </Button>
                  <Typography variant="caption" tone="muted">
                    {t("incidentForm.submitHint")}
                  </Typography>
                </div>
              </>
            )}
          </div>
        </Card.Body>
      </Card>
    </section>
  );
}

interface RaisedSummaryProps {
  /** What was filed, when this card filed it; null after a remount. */
  body: IncidentCreateRequest | null;
  /** The streamed prefill, shown when the filed body is no longer in memory. */
  fallbackDetails: string;
  ticket: TicketRef;
  /** The colleague it was raised for; null when it is the reporter's own. */
  raisedFor: string | null;
}

/**
 * The card after the incident is raised.
 *
 * The inputs are replaced rather than left disabled: a greyed-out form that
 * still looks fillable reads worse than a plain record of what was sent.
 */
function RaisedSummary({ body, fallbackDetails, ticket, raisedFor }: RaisedSummaryProps) {
  const { t } = useT("assistant");
  const { t: tt } = useT("tickets");

  const answered = (body?.followups ?? []).filter((item) => (item.answer ?? "").trim() !== "");
  const items = [
    { label: tt("field.subject"), value: body?.subject ?? "" },
    { label: tt("incident.details.label"), value: body?.description ?? fallbackDetails },
    ...answered.map((item) => ({ label: item.question, value: item.answer ?? "" })),
    { label: tt("incident.more.summaryLabel"), value: body?.additional_details ?? "" },
  ]
    .filter((item) => item.value.trim() !== "")
    .map((item) => ({
      label: item.label,
      value: <span className="break-words whitespace-pre-wrap">{item.value}</span>,
    }));

  return (
    <div className="flex flex-col gap-4">
      {raisedFor === null ? (
        <>
          <Alert tone="success" title={t("incidentForm.submittedTitle")}>
            {t("incidentForm.submittedBody", { number: ticket.ticket_number })}
          </Alert>
          <KioskTicketNumberNote number={ticket.ticket_number} />
          <TicketRefCard ticket={ticket} />
        </>
      ) : (
        // Theirs, not the reporter's: no link into a ticket they cannot open.
        <Alert tone="success" title={tt("onBehalf.raisedTitle", { name: raisedFor })}>
          {tt("onBehalf.raised", { name: raisedFor, number: ticket.ticket_number })}{" "}
          {tt("onBehalf.notInList")}
        </Alert>
      )}

      {items.length === 0 ? null : (
        <section aria-label={t("incidentForm.summaryTitle")} className="flex flex-col gap-2">
          <Typography as="h4" variant="label">
            {t("incidentForm.summaryTitle")}
          </Typography>
          <DescriptionList items={items} orientation="horizontal" />
        </section>
      )}
    </div>
  );
}

export interface IncidentFormListProps {
  forms: AssistantIncidentFormEvent[];
  /** Prefix for each card's cache key — the assistant message id. */
  scopeId: string;
  conversationId?: string | null;
  attachmentIds?: string[];
  className?: string;
}

/** Every incident report the assistant offered in one turn. Nothing when empty. */
export function IncidentFormList({
  forms,
  scopeId,
  conversationId = null,
  attachmentIds = [],
  className,
}: IncidentFormListProps) {
  if (forms.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {forms.map((form, index) => (
        <IncidentFormCard
          // No offering id to key on — see `AssistantIncidentFormEvent`. The
          // list is append-only within a turn, so the position is stable.
          key={`${scopeId}:${index}`}
          form={form}
          formKey={`${scopeId}:incident:${index}`}
          conversationId={conversationId}
          attachmentIds={attachmentIds}
        />
      ))}
    </div>
  );
}
